import {
  Alert,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@beweegbaas/ui";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { useServerFn } from "#/modules/util.hooks/use-server-fn";
import { useState } from "react";
import { m } from "#/paraglide/messages";
import { LayoutAuth } from "#/layouts/layout.auth";
import {
  type ConfirmEmailResendResult,
  resendConfirmation,
} from "#/modules/util.confirm-email/confirm-email-resend.functions";
import { z } from "zod/v4";

export const Route = createFileRoute("/$locale/{-$schoolyear}/confirm-email/resend/")({
  validateSearch: z.object({
    email: z.string().optional().catch(undefined),
  }),
  // No email to resend to → back to login (portal parity).
  beforeLoad: ({ search, params }) => {
    if (!search.email) {
      throw redirect({
        to: "/$locale/{-$schoolyear}/login",
        params: { locale: params.locale, schoolyear: params.schoolyear },
      });
    }
  },
  component: ConfirmEmailResendPage,
});

function ConfirmEmailResendPage() {
  const { email } = Route.useSearch();
  const submit = useServerFn(resendConfirmation);
  const [result, setResult] = useState<ConfirmEmailResendResult | null>(null);
  const [pending, setPending] = useState(false);
  const pageTitle = m["auth.confirm_email.resend.page_title"]();

  // email is guaranteed present by beforeLoad.
  const address = email ?? "";

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setPending(true);
    try {
      const submitted = await submit({ data: { email: address } });
      if (submitted) setResult(submitted);
    } finally {
      setPending(false);
    }
  };

  // Successfully resent confirmation email
  if (result?.success === true) {
    return (
      <LayoutAuth>
        <title>{pageTitle}</title>
        <Card className="relative w-full">
          <CardHeader>
            <Alert variant="default" className="w-full border-none bg-green-100">
              <IconMaterialSymbolsCheckCircleRounded className="text-green-600!" />
              {m["auth.confirm_email.resent_confirmation_email_title"]({ email: address })}
              <CardDescription>
                {m["auth.confirm_email.resent_confirmation_email_message"]({})}
              </CardDescription>
            </Alert>
          </CardHeader>
        </Card>
      </LayoutAuth>
    );
  }

  // Email already confirmed
  if (result?.success === false && result.emailAlreadyConfirmed) {
    return (
      <LayoutAuth>
        <title>{pageTitle}</title>
        <Card className="relative w-full">
          <CardHeader>
            <CardTitle>{m["auth.api_errors.email_already_confirmed"]()}</CardTitle>
            <CardDescription>{result.message}</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <p className="text-muted-foreground text-sm">
              {m["auth.confirm_email.resend.already_confirmed_description"]()}
            </p>
            <Button
              render={
                <Link
                  from={Route.fullPath}
                  to="/$locale/{-$schoolyear}/login"
                  params={(prev: Record<string, string>) => prev}
                />
              }
              nativeButton={false}
            >
              {m["common.auth.back_to_login"]()}
            </Button>
          </CardContent>
        </Card>
      </LayoutAuth>
    );
  }

  // Unhandled error occurred
  if (result?.success === false) {
    return (
      <LayoutAuth>
        <title>{pageTitle}</title>
        <Card className="relative w-full">
          <CardHeader>
            <CardTitle className="text-destructive flex items-center gap-2">
              <IconMaterialSymbolsError className="text-red-500" />
              {m["common.errors.title"]()}
            </CardTitle>
            <CardDescription className="text-destructive">{result.message}</CardDescription>
          </CardHeader>
          <CardContent>
            <form noValidate onSubmit={onSubmit} className="flex flex-col gap-4">
              <Button type="submit" variant="destructive" isPending={pending}>
                {m["common.actions.try_again"]()}
              </Button>
            </form>
          </CardContent>
        </Card>
      </LayoutAuth>
    );
  }

  // Resend confirmation email ui
  return (
    <LayoutAuth>
      <title>{pageTitle}</title>
      <Card className="relative w-full">
        <CardHeader>
          <CardTitle>{m["auth.confirm_email.resend.title"]()}</CardTitle>
          <CardDescription>
            {m["auth.confirm_email.resend.description"]({ email: address })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form noValidate onSubmit={onSubmit} className="flex flex-col gap-4">
            <Button type="submit" variant="default" isPending={pending}>
              {m["auth.confirm_email.resend.submit_button"]()}
            </Button>
          </form>
        </CardContent>
      </Card>
    </LayoutAuth>
  );
}
